(() => {
  const triggerSelector = '[data-ods-size-chart-trigger-container]';
  const triggerSourceSelector = '[data-ods-size-chart-trigger-source]';
  const triggerCloneSelector = '[data-ods-size-chart-trigger-clone]';
  const triggerButtonSelector = `${triggerSelector} .ods-size-chart__trigger[aria-controls]`;
  const mountSelector = '[data-ods-size-chart-mount]';
  const tabsSelector = '[data-ods-size-chart-tabs]';
  const dialogSelector = '[data-ods-size-chart-dialog]';
  const tabActivationIds = new WeakMap();
  const triggerSourceNodes = new WeakMap();

  function triggerControl(container) {
    return container.querySelector?.('.ods-size-chart__trigger[aria-controls]')?.getAttribute('aria-controls');
  }

  function syncTriggerExpanded(dialog, expanded = dialog.open) {
    document.querySelectorAll(triggerButtonSelector).forEach((trigger) => {
      if (trigger.getAttribute('aria-controls') === dialog.id) {
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
    });
  }

  function reconcileSizeChartTrigger() {
    const sources = [...document.querySelectorAll(triggerSourceSelector)];
    const clones = [...document.querySelectorAll(triggerCloneSelector)];
    const source = sources[sources.length - 1];
    if (!source) {
      clones.forEach((clone) => clone.remove());
      return;
    }

    const mount = document.querySelector(mountSelector);
    const sourceControl = triggerControl(source);

    sources.forEach((candidate) => { candidate.hidden = candidate !== source || Boolean(mount); });

    if (!mount) {
      clones.forEach((clone) => clone.remove());
      source.hidden = false;
      document.querySelectorAll(dialogSelector).forEach((dialog) => syncTriggerExpanded(dialog));
      return;
    }

    let mountedTrigger = clones.find((clone) => (
      mount.contains(clone)
      && triggerSourceNodes.get(clone) === source
      && triggerControl(clone) === sourceControl
    ));

    clones.forEach((clone) => {
      if (clone !== mountedTrigger) clone.remove();
    });

    if (!mountedTrigger) {
      mountedTrigger = source.cloneNode(true);
      mountedTrigger.removeAttribute('data-ods-size-chart-trigger-source');
      mountedTrigger.setAttribute('data-ods-size-chart-trigger-clone', '');
      triggerSourceNodes.set(mountedTrigger, source);
      mount.append(mountedTrigger);
    }

    mountedTrigger.hidden = false;
    document.querySelectorAll(dialogSelector).forEach((dialog) => syncTriggerExpanded(dialog));
  }

  function activateTab(tabs, selectedTab, moveFocus = false, animatePanel = true) {
    const tabButtons = [...tabs.querySelectorAll('[role="tab"]')];
    const tabPanels = [...tabs.querySelectorAll('[role="tabpanel"]')];
    const controlledPanel = selectedTab.getAttribute('aria-controls');
    const selectionChanged = !selectedTab.matches('[aria-selected="true"]');
    const activationId = (tabActivationIds.get(tabs) || 0) + 1;

    tabActivationIds.set(tabs, activationId);

    tabButtons.forEach((tab) => {
      const selected = tab === selectedTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
    tabPanels.forEach((panel) => {
      panel.classList.remove('is-entering');
      panel.hidden = panel.id !== controlledPanel;
    });

    if (animatePanel && selectionChanged) {
      requestAnimationFrame(() => {
        const panel = document.getElementById(controlledPanel);
        if (
          tabActivationIds.get(tabs) === activationId
          && panel
          && tabs.contains(panel)
          && !panel.hidden
          && selectedTab.matches('[aria-selected="true"]')
        ) {
          panel.classList.add('is-entering');
        }
      });
    }

    if (moveFocus) selectedTab.focus();
  }

  function resetTabs(tabs) {
    const defaultTab = tabs.querySelector('[role="tab"]');
    if (defaultTab) activateTab(tabs, defaultTab, false, false);
  }

  function initDiagramLightbox(tabs, drawerDialog) {
    const lightboxHost = document.querySelector(`[data-ods-size-chart-lightbox="${drawerDialog.id}"]`);
    if (!lightboxHost) return;

    let activeLightBox = null;
    lightboxHost.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (activeLightBox?.pswp) activeLightBox.pswp.close();
      else lightboxHost.close();
    });

    tabs.querySelectorAll('.ods-size-chart__diagram-button').forEach((button) => {
      const opener = button.closest('wrapp-open-pswp-btn');
      let boundLightBox = null;

      button.addEventListener('click', () => {
        const lightBox = opener?.lightBox;
        if (!lightBox?.options) return;

        activeLightBox = lightBox;
        if (boundLightBox !== lightBox) {
          boundLightBox = lightBox;
          // This target listener runs before the click bubbles to Kalles'
          // wrapper, so PhotoSwipe receives its top-layer host before opening.
          lightBox.options.appendToEl = lightboxHost;
          lightBox.on?.('afterInit', () => {
            requestAnimationFrame(() => lightBox.pswp?.updateSize(true));
          });
          lightBox.on?.('destroy', () => {
            if (lightboxHost.open) lightboxHost.close();
          });
        }

        if (!lightboxHost.open) lightboxHost.showModal();
      });
    });
  }

  function initTabs(root = document) {
    const tabGroups = [
      ...(root.matches?.(tabsSelector) ? [root] : []),
      ...(root.querySelectorAll?.(tabsSelector) || [])
    ];

    tabGroups.forEach((tabs) => {
      if (tabs.dataset.odsSizeChartTabsReady === 'true') return;
      tabs.dataset.odsSizeChartTabsReady = 'true';

      const tabButtons = [...tabs.querySelectorAll('[role="tab"]')];
      tabs.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
        panel.addEventListener('animationend', (event) => {
          if (event.target === panel && event.animationName === 'ods-size-chart-panel-in') {
            panel.classList.remove('is-entering');
          }
        });
      });
      tabButtons.forEach((tab) => {
        tab.addEventListener('click', () => activateTab(tabs, tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

          event.preventDefault();
          const currentIndex = tabButtons.indexOf(tab);
          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabButtons.length;
          if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabButtons.length - 1;
          activateTab(tabs, tabButtons[nextIndex], true);
        });
      });

      resetTabs(tabs);
    });
  }

  function initDrawers(root = document) {
    const dialogs = [
      ...(root.matches?.(dialogSelector) ? [root] : []),
      ...(root.querySelectorAll?.(dialogSelector) || [])
    ];

    dialogs.forEach((dialog) => {
      if (dialog.dataset.odsSizeChartDrawerReady === 'true') {
        syncTriggerExpanded(dialog);
        return;
      }
      dialog.dataset.odsSizeChartDrawerReady = 'true';

      const resetOptionalTabs = () => {
        const tabs = dialog.querySelector(tabsSelector);
        if (tabs) resetTabs(tabs);
      };
      const tabs = dialog.querySelector(tabsSelector);
      if (tabs) initDiagramLightbox(tabs, dialog);

      // Kalles keeps the native dialog open until its closing animation ends.
      // Mirror that lifecycle even when the drawer has no optional tab group.
      dialog.addEventListener('dialog:opening', () => {
        syncTriggerExpanded(dialog, true);
        resetOptionalTabs();
      });
      dialog.addEventListener('toggle', () => {
        syncTriggerExpanded(dialog);
        if (dialog.open) resetOptionalTabs();
      });
      dialog.addEventListener('close', () => {
        syncTriggerExpanded(dialog, false);
        resetOptionalTabs();
      });
      syncTriggerExpanded(dialog);
    });
  }

  function init(root = document) {
    reconcileSizeChartTrigger();
    initTabs(root);
    initDrawers(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
